// UTM tag capture.
//
// The Countly web SDK already captures utm_* on its own, but only from the URL
// that is present at the moment an app instance is init'ed, and only through
// the getSearchQuery callback it is configured with (see utils/mixins/analytics.ts).
// That covers the USER app, which is init'ed on boot, but not the WORKSPACE and
// LANDING apps: those are init'ed later, on setDeviceId, by which point the user
// has navigated away from the landing URL and the tags are gone.
//
// So the tags are also mirrored into localStorage on every entry page and
// replayed from there when the workspace profile is written during onboarding
// (views/dashboard/profile/OnboardingCreateCompany.vue).

const STORAGE_KEY = "utm_tags";

// The five tags the SDK tracks by default (its `utm` init option). Keeping the
// same five here means the SDK-captured properties and the ones replayed onto
// the workspace profile carry identical names.
export const UTM_TAG_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type UtmTag = { key: string; value: string };

/**
 * The full query string of the current URL, as `a=b&c=d` (no leading "?").
 *
 * The router runs in hash mode, so a marketing link can carry its tags either
 * before the hash (`/?utm_source=x#/account/create`) or inside it
 * (`/#/account/create?utm_source=x`). Both are merged, hash last so that it
 * wins on a duplicate key — which is also what the SDK does when it parses the
 * string, since it overwrites as it iterates.
 */
export function getUrlQuery(): string {
  const search = window.location.search.replace(/^\?/, "");
  const hashQuery = window.location.hash.split("?")[1] || "";
  return [search, hashQuery].filter(Boolean).join("&");
}

// Deliberately not URLSearchParams: the SDK splits on "&" and "=" and stores
// the value as-is, undecoded (`spring+sale` stays `spring+sale`). Parsing the
// same way keeps the values written here byte-identical to the utm_* properties
// the SDK writes, so the two can be segmented on together.
function parseQueryLikeSdk(query: string): Record<string, string> {
  const params: Record<string, string> = {};
  query.split("&").forEach((part) => {
    const [key, value] = part.split("=");
    if (key && value) params[key] = value;
  });
  return params;
}

export function readUtmTagsFromUrl(): UtmTag[] {
  const params = parseQueryLikeSdk(getUrlQuery());
  return UTM_TAG_KEYS.filter((key) => params[key]).map((key) => ({ key, value: params[key] }));
}

/**
 * Store the tags of the current URL, if it has any. A URL without tags leaves
 * whatever was stored earlier untouched: a user who lands with tags and then
 * navigates to another entry page before signing up should keep them. A URL
 * with tags replaces them (last touch), matching what the SDK does.
 *
 * Storage access is guarded throughout: with site data blocked, localStorage
 * throws, and these run first thing in mounted() ahead of the Google Sign-In
 * setup, which must not be skipped over a lost tag.
 */
export function persistUtmTags() {
  const tags = readUtmTagsFromUrl();
  if (!tags.length) return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tags));
  } catch {
    // Storage unavailable — the SDK-side capture still happens.
  }
}

export function getPersistedUtmTags(): UtmTag[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Storage unavailable, or a malformed entry — treat it as absent.
    return [];
  }
}

export function clearPersistedUtmTags() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}
